// ===========================================
// School event dialog (in-app calendars, #256)
// ===========================================
// Shared by the admin/teacher calendar. The parent calendar has its own dialog (RSVP chips).

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { CalendarEvent } from '../../api/hybridBooking.api';
import { usePermissions } from '../../hooks/usePermissions';
import { useTenant, useSchoolTimezone } from '../../contexts/TenantContext';
import AddToCalendarButtons from './AddToCalendarButtons';
import { formatEventWhen } from './eventCalendarItem';

interface SchoolEventDialogProps {
  event: CalendarEvent | null;
  onClose: () => void;
}

export default function SchoolEventDialog({ event, onClose }: SchoolEventDialogProps) {
  const { can } = usePermissions();
  const { schoolSlug } = useTenant();
  const timeZone = useSchoolTimezone();
  if (!event) return null;
  const r = event.resource;
  const eventId = r?.eventId;
  const venue = [r?.venueName, r?.roomName, r?.locationName].filter(Boolean).join(', ');

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography
          variant="h6"
          component="span"
          sx={{ textDecoration: r?.isCancelled ? 'line-through' : undefined }}
        >
          {event.title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          {r?.isCancelled && <Chip label="Cancelled" color="error" size="small" sx={{ alignSelf: 'flex-start' }} />}
          <Typography variant="body2">
            <strong>When:</strong> {formatEventWhen(event.startsAtIso, event.endsAtIso, timeZone)}
          </Typography>
          {venue && (
            <Typography variant="body2">
              <strong>Where:</strong> {venue}
            </Typography>
          )}
          {eventId && event.startsAtIso && (
            <AddToCalendarButtons
              eventId={eventId}
              title={event.title}
              startsAtIso={event.startsAtIso}
              endsAtIso={event.endsAtIso}
              location={venue || null}
              available={r?.eventStatus === 'PUBLISHED'}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {eventId && can('events', 'read') && (
          <Button component={RouterLink} to={`/${schoolSlug}/admin/events/${eventId}`}>
            View event
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
